import { useCallback, useRef, useState } from 'react';

const MIN_RESIZE_WIDTH_PX = 40;

interface ResizingState {
  columnIndex: number;
  startWidth: number;
  startX: number;
}

export function useColumnWidths(
  initialColumnWidths: Record<number, number> = {},
) {
  const [columnWidths, setColumnWidths] =
    useState<Record<number, number>>(initialColumnWidths);
  const resizingRef = useRef<ResizingState | null>(null);

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const resizing = resizingRef.current;
    if (!resizing) {
      return;
    }

    const nextWidth = Math.max(
      MIN_RESIZE_WIDTH_PX,
      resizing.startWidth + (event.clientX - resizing.startX),
    );

    setColumnWidths((previous) => ({
      ...previous,
      [resizing.columnIndex]: nextWidth,
    }));
  }, []);

  const stopResizing = useCallback(() => {
    resizingRef.current = null;
    document.body.style.removeProperty(`cursor`);
    document.body.style.removeProperty(`user-select`);
    window.removeEventListener(`pointermove`, handlePointerMove);
    window.removeEventListener(`pointerup`, stopResizing);
  }, [handlePointerMove]);

  const startResizing = useCallback(
    (columnIndex: number, startWidth: number, startX: number) => {
      resizingRef.current = { columnIndex, startWidth, startX };
      document.body.style.cursor = `col-resize`;
      document.body.style.userSelect = `none`;
      window.addEventListener(`pointermove`, handlePointerMove);
      window.addEventListener(`pointerup`, stopResizing);
    },
    [handlePointerMove, stopResizing],
  );

  const getColumnWidthStyle = useCallback(
    (columnIndex: number) => {
      const width = columnWidths[columnIndex];

      if (!width) {
        return undefined;
      }

      return {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
      };
    },
    [columnWidths],
  );

  return { columnWidths, getColumnWidthStyle, startResizing };
}
